import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail, downloadExport } from "../lib/api";
import { PageHeader, EmptyState, Confirm } from "../components/Shared";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../components/ui/hover-card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { EditableListSelect } from "../components/EditableListSelect";
import PhoneField from "../components/PhoneField";
import { Users, Eye, Pencil, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import { fmtFCFA, ROLES } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { isValidEmail, isValidPhone } from "../lib/validate";

const F = ({ label, required, children }) => (
  <div className="space-y-1.5"><Label className="text-sm">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</Label>{children}</div>
);

const SubEmpList = ({ c }) => (
  <>
    <div className="text-xs font-semibold text-slate-700 mb-2">Sub-employees under {c.name}</div>
    {c.sub_employees?.length > 0 ? (
      <ul className="space-y-1.5 max-h-56 overflow-auto">
        {c.sub_employees.map((s) => (
          <li key={s.id} className="flex items-center justify-between gap-3 text-xs" data-testid={`contractor-subemployee-${s.id}`}>
            <span className="font-semibold text-blue-600">{s.name}</span>
            <span className="flex items-center gap-2">
              <span className="text-slate-400">{s.profession || "—"}</span>
              <span className="font-medium text-green-600">{s.phone || "—"}</span>
            </span>
          </li>
        ))}
      </ul>
    ) : (
      <div className="text-xs text-slate-400">No sub-employees linked.</div>
    )}
  </>
);

const StatusChip = ({ status }) => {
  const s = (status || "").toLowerCase();
  const map = { active: "bg-green-100 text-green-700", pending: "bg-amber-100 text-amber-700", rejected: "bg-red-100 text-red-700" };
  return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${map[s] || "bg-slate-100 text-slate-600"}`}>{status ? status.charAt(0).toUpperCase() + status.slice(1) : "—"}</span>;
};

export default function Contractors() {
  const { isRole } = useAuth();
  const qc = useQueryClient();
  const canManage = isRole(ROLES.CEO);
  const { data: contractors = [] } = useQuery({ queryKey: ["contractors"], queryFn: () => api.get("/contractors").then((r) => r.data) });

  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null); // full employee record
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [hoverId, setHoverId] = useState(null);

  const openEdit = async (c) => {
    try {
      const emp = await api.get(`/employees/${c.id}`).then((r) => r.data);
      setEditing(emp);
      setForm({ ...emp, gender: emp.gender || "Male" });
      setErrors({});
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const save = async () => {
    const errs = {};
    if (!form.name?.trim()) errs.name = "Name is required.";
    if (!form.email?.trim()) errs.email = "Email is required.";
    else if (!isValidEmail(form.email)) errs.email = "Enter a valid email address.";
    if (!form.phone) errs.phone = "Telephone is required.";
    else if (!isValidPhone(form.phone)) errs.phone = "Enter a valid phone number.";
    if (!form.gender) errs.gender = "Gender is required.";
    if (!form.profession) errs.profession = "Profession is required.";
    if (Object.keys(errs).length) { setErrors(errs); toast.error("Please complete all required fields."); return; }
    setErrors({});
    try {
      await api.put(`/employees/${editing.id}`, { ...editing, ...form, staff_category: editing.staff_category });
      toast.success("Contractor updated");
      setEditing(null); setForm(null);
      qc.invalidateQueries({ queryKey: ["contractors"] });
      qc.invalidateQueries({ queryKey: ["employees"] });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/employees/${c.id}`);
      toast.success("Contractor deleted");
      qc.invalidateQueries({ queryKey: ["contractors"] });
      qc.invalidateQueries({ queryKey: ["employees"] });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="contractors-page">
      <PageHeader title="Contractors" subtitle="Sub-contractors directory" />

      {contractors.length === 0 ? (
        <EmptyState title="No contractors yet" description="Sub-contractors added under the Subcontractor category will appear here." />
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Telephone</TableHead>
                <TableHead>Gender</TableHead>
                <TableHead>Profession</TableHead>
                <TableHead className="text-center">Contracts</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contractors.map((c) => (
                <TableRow key={c.id} data-testid={`contractor-row-${c.id}`} className="cursor-default"
                  onMouseEnter={() => setHoverId(c.id)} onMouseLeave={() => setHoverId(null)}>
                  <TableCell className="font-medium">
                    <HoverCard open={hoverId === c.id}>
                      <HoverCardTrigger asChild>
                        <span className="inline-flex items-center gap-1.5" data-testid={`contractor-name-${c.id}`}>
                          {c.name}
                          {c.sub_employees?.length > 0 && (
                            <span className="inline-flex items-center gap-0.5 rounded-full bg-slate-100 px-1.5 text-[10px] font-semibold text-slate-500">
                              <Users className="h-3 w-3" />{c.sub_employees.length}
                            </span>
                          )}
                        </span>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-72" align="start" data-testid={`contractor-subemployees-${c.id}`}
                        onMouseEnter={() => setHoverId(c.id)} onMouseLeave={() => setHoverId(null)}>
                        <SubEmpList c={c} />
                      </HoverCardContent>
                    </HoverCard>
                  </TableCell>
                  <TableCell className="text-slate-600">{c.email || "—"}</TableCell>
                  <TableCell className="text-slate-600">{c.phone || "—"}</TableCell>
                  <TableCell className="text-slate-600">{c.gender || "—"}</TableCell>
                  <TableCell className="text-slate-600">{c.profession || "—"}</TableCell>
                  <TableCell className="text-center text-slate-600">{c.contracts?.length || 0}</TableCell>
                  <TableCell className="text-right">
                    <div className="stems-actions">
                      <Button size="icon" variant="ghost" data-testid={`contractor-view-${c.id}`} onClick={() => setViewing(c)}><Eye className="h-4 w-4" /></Button>
                      {canManage && <Button size="icon" variant="ghost" data-testid={`contractor-edit-${c.id}`} onClick={() => openEdit(c)}><Pencil className="h-4 w-4" /></Button>}
                      {canManage && <Confirm onConfirm={() => remove(c)} title="Delete contractor?" description={`This removes "${c.name}". Contractors with linked sub-employees cannot be deleted.`}
                        trigger={<Button size="icon" variant="ghost" data-testid={`contractor-delete-${c.id}`} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>} />}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* View dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => { if (!o) setViewing(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="contractor-view-dialog">
          <DialogHeader><DialogTitle>{viewing?.name}</DialogTitle>
            <DialogDescription>Contractor details, linked sub-employees and contracts by project.</DialogDescription>
          </DialogHeader>
          {viewing && (
            <div className="space-y-5">
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                <VRow label="Email">{viewing.email || "—"}</VRow>
                <VRow label="Telephone"><span className="text-green-600 font-medium">{viewing.phone || "—"}</span></VRow>
                <VRow label="Gender">{viewing.gender || "—"}</VRow>
                <VRow label="Profession">{viewing.profession || "—"}</VRow>
              </div>

              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Sub-employees</p>
                {viewing.sub_employees?.length > 0 ? (
                  <ul className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                    {viewing.sub_employees.map((s) => (
                      <li key={s.id} className="flex items-center justify-between px-4 py-2 text-sm" data-testid={`contractor-view-sub-${s.id}`}>
                        <span className="font-semibold text-blue-600">{s.name}</span>
                        <span className="flex items-center gap-3">
                          <span className="text-slate-500 text-xs">{s.profession || "—"}</span>
                          <span className="text-green-600 font-medium">{s.phone || "—"}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : <p className="text-sm text-slate-400">No sub-employees linked.</p>}
              </div>

              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Contracts by project</p>
                {viewing.contracts?.length > 0 ? (
                  <div className="rounded-lg border border-slate-200 overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Project</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead className="text-right">Advance</TableHead>
                          <TableHead className="text-right">Balance</TableHead>
                          <TableHead className="text-center">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {viewing.contracts.map((ct) => (
                          <TableRow key={ct.id} data-testid={`contractor-contract-${ct.id}`}>
                            <TableCell className="font-medium text-slate-800">{ct.project_name}</TableCell>
                            <TableCell className="text-right font-mono-num text-blue-600">{fmtFCFA(ct.amount)}</TableCell>
                            <TableCell className="text-right font-mono-num text-amber-600">{fmtFCFA(ct.advance)}</TableCell>
                            <TableCell className={`text-right font-mono-num font-semibold ${ct.balance < 0 ? "text-red-600" : "text-green-600"}`}>{fmtFCFA(ct.balance)}</TableCell>
                            <TableCell className="text-center"><StatusChip status={ct.status} /></TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-slate-50" data-testid="contractor-contract-totals">
                          <TableCell className="font-semibold text-slate-700">Total</TableCell>
                          <TableCell className="text-right font-mono-num font-semibold text-blue-700">{fmtFCFA(viewing.contract_totals?.amount)}</TableCell>
                          <TableCell className="text-right font-mono-num font-semibold text-amber-700">{fmtFCFA(viewing.contract_totals?.advance)}</TableCell>
                          <TableCell className={`text-right font-mono-num font-semibold ${(viewing.contract_totals?.balance || 0) < 0 ? "text-red-700" : "text-green-700"}`}>{fmtFCFA(viewing.contract_totals?.balance)}</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                ) : <p className="text-sm text-slate-400">No contracts for this contractor yet.</p>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewing(null)} data-testid="contractor-view-close">Close</Button>
            <Button data-testid="contractor-view-pdf" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"
              onClick={() => downloadExport(`/export/contractor/${viewing.id}/pdf`, `contractor_${(viewing.name || viewing.id).replace(/\s+/g, "_")}.pdf`)}>
              <FileText className="h-4 w-4 mr-2" /> Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); setForm(null); } }}>
        <DialogContent className="max-w-xl" data-testid="contractor-edit-dialog">
          <DialogHeader><DialogTitle>Edit Contractor</DialogTitle>
            <DialogDescription>Update the contractor's contact and profile details.</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <F label="Name" required>
                <Input data-testid="contractor-name-input" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className={errors.name ? "border-red-400" : ""} />
                {errors.name && <p className="text-xs text-red-600">{errors.name}</p>}
              </F>
              <F label="Email" required>
                <Input data-testid="contractor-email-input" type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className={errors.email ? "border-red-400" : ""} />
                {errors.email && <p className="text-xs text-red-600">{errors.email}</p>}
              </F>
              <F label="Telephone" required>
                <PhoneField value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="contractor-phone-input" invalid={!!errors.phone} />
                {errors.phone && <p className="text-xs text-red-600">{errors.phone}</p>}
              </F>
              <F label="Gender" required>
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger data-testid="contractor-gender-input"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
                </Select>
              </F>
              <F label="Profession" required>
                <EditableListSelect value={form.profession || ""} onValueChange={(v) => setForm({ ...form, profession: v })}
                  endpoint="/professions" noun="profession" canManage={canManage} placeholder="Select profession" testPrefix="contractor-profession" />
                {errors.profession && <p className="text-xs text-red-600">{errors.profession}</p>}
              </F>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditing(null); setForm(null); }}>Cancel</Button>
            <Button data-testid="contractor-edit-submit" onClick={save} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const VRow = ({ label, children }) => (
  <div className="flex items-center justify-between px-4 py-2.5 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="font-medium text-slate-800 text-right">{children}</span>
  </div>
);
